import { useEffect, useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import {
  AreaChart, Area, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts'
import { TrendingUp, BarChart2, Globe, Clock, Zap, Activity, Search } from 'lucide-react'
import PlatformCapabilities, { PLATFORMS_LIST } from './PlatformCapabilities'

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="relative overflow-hidden p-5 bg-[#111111] border border-[#2a2a2a] rounded-xl">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3a3a3a] to-transparent" />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-[#6b6b6b] uppercase tracking-widest mb-2 font-medium">{label}</p>
          <p className="text-3xl font-bold text-white tabular-nums">{value}</p>
          {sub && <p className="text-xs text-[#5a5a5a] mt-1">{sub}</p>}
        </div>
        <div className="p-2.5 bg-[#1e1e1e] rounded-lg border border-[#2a2a2a]">
          <Icon className="w-5 h-5 text-[#7a7a7a]" />
        </div>
      </div>
    </div>
  )
}

export default function WelcomeDashboard({ onNavigate, selectedCountries = [] }) {
  const { t } = useLanguage()
  const [apiStatus, setApiStatus] = useState({})
  const [configuredCount, setConfiguredCount] = useState(0)

  useEffect(() => {
    // Use relative URL — works from any browser since nginx proxies /api
    fetch('/api/health')
      .then(r => r.json())
      .then(data => {
        if (data.apis) {
          const status = {}
          let count = 0
          for (const [id, info] of Object.entries(data.apis)) {
            status[id] = info.status === 'ok'
            if (info.status === 'ok') count++
          }
          setApiStatus(status)
          setConfiguredCount(count)
        }
      })
      .catch(() => {
        // Minimal fallback — google_trends always works
        setApiStatus({ google_trends: true })
        setConfiguredCount(1)
      })
  }, [])

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Hero title */}
      <div className="pt-2 pb-1">
        <h2 className="text-2xl font-bold text-white tracking-tight">
          Global Market Insight Engine
        </h2>
        <p className="text-sm text-[#6b6b6b] mt-1">
          {t('dashboard.platformSubtitle')}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={t('dashboard.apisActive')}
          value={`${configuredCount}/${PLATFORMS_LIST.length}`}
          sub={configuredCount < PLATFORMS_LIST.length ? t('dashboard.configureInSettings') : t('dashboard.allConnected')}
          icon={Zap}
        />
        <StatCard
          label={t('dashboard.platforms')}
          value={String(PLATFORMS_LIST.length)}
          sub={t('dashboard.dataSources')}
          icon={Activity}
        />
        <StatCard
          label={t('dashboard.markets')}
          value={selectedCountries.length}
          sub={selectedCountries.length > 0 ? selectedCountries.join(' · ') : '—'}
          icon={Globe}
        />
        <StatCard
          label={t('dashboard.analysisTime')}
          value="~8s"
          sub={t('dashboard.perSearch')}
          icon={Clock}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Platform readiness */}
        <div className="p-5 bg-[#111111] border border-[#2a2a2a] rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1e1e1e] rounded-lg border border-[#2a2a2a]">
              <BarChart2 className="w-4 h-4 text-[#7a7a7a]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-white uppercase tracking-widest">{t('dashboard.platformStatus')}</p>
              <p className="text-xs text-[#5a5a5a]">{t('dashboard.configuredPlatforms')}</p>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {PLATFORMS_LIST.map(p => {
              const Icon = p.icon
              const active = apiStatus[p.id]
              return (
                <div key={p.id} className="flex items-center gap-3">
                  <Icon className="w-3.5 h-3.5 text-[#6b6b6b] shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[#aaaaaa] font-medium">{p.name}</span>
                      <span className={`text-xs font-medium ${active ? 'text-[#c0c0c0]' : 'text-[#4a4a4a]'}`}>
                        {active ? t('dashboard.active') : t('dashboard.pending')}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#1e1e1e] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: active ? '100%' : '0%',
                          background: active
                            ? 'linear-gradient(90deg, #5a5a5a, #c0c0c0)'
                            : '#2a2a2a',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="mt-4 pt-3 border-t border-[#1e1e1e] flex items-center justify-between">
            <span className="text-xs text-[#5a5a5a]">
              {configuredCount} / {PLATFORMS_LIST.length} {t('dashboard.activePlatforms')}
            </span>
            <span className="text-xs text-[#7a7a7a] font-medium">
              {Math.round((configuredCount / PLATFORMS_LIST.length) * 100)}% {t('dashboard.configured')}
            </span>
          </div>
        </div>

        {/* Global Trend — empty state until a real search is performed */}
        <div className="p-5 bg-[#111111] border border-[#2a2a2a] rounded-xl flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1e1e1e] rounded-lg border border-[#2a2a2a]">
              <TrendingUp className="w-4 h-4 text-[#7a7a7a]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-white uppercase tracking-widest">{t('dashboard.globalTrend')}</p>
              <p className="text-xs text-[#5a5a5a]">{t('dashboard.searchTrendDesc')}</p>
            </div>
          </div>

          {/* Empty state */}
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
              <Search className="w-5 h-5 text-[#444444]" />
            </div>
            <div>
              <p className="text-xs text-[#4a4a4a] font-medium">{t('dashboard.noSearchYet')}</p>
              <p className="text-xs text-[#333333] mt-0.5">{t('dashboard.trendAfterSearch')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform capability tiles — only activate when search results exist for that platform */}
      <PlatformCapabilities apiStatus={apiStatus} results={null} />

      {/* How to start */}
      <div className="flex items-center gap-4 p-4 bg-[#0d0d0d] border border-[#1e1e1e] rounded-xl">
        <div className="p-2.5 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a] shrink-0">
          <Zap className="w-4 h-4 text-[#7a7a7a]" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#cccccc]">{t('dashboard.readyToAnalyze')}</p>
          <p className="text-xs text-[#555555] mt-0.5">
            {t('dashboard.readyDesc')}{' '}
            <span className="text-[#888]">{t('dashboard.settingsPath')}</span>.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-[#444]">Google Trends</p>
          <p className="text-xs text-[#555] font-medium">{t('dashboard.activeWithoutConfig')}</p>
        </div>
      </div>

    </div>
  )
}
